import { Button, Chip, Modal, Skeleton, Spinner } from "@heroui/react";
import { useState } from "react";

import { AccountBadge } from "@/src/components/AccountBadge";
import type { AccountFace } from "@/src/components/AccountBadge";
import { useAdminConversation } from "@/src/hooks/useAdminConversation";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import type { Locale } from "@/src/i18n/translate";
import type {
  ConversationMessage,
  ConversationReaction,
} from "@/src/libs/moderation.type";
import { relativeTime } from "@/src/libs/relativeTime";

/**
 * L'auteur d'un message, dans la forme du badge.
 *
 * `null` quand le compte a été supprimé : le serveur met alors `accountId`
 * **et** `username` à `null`, et un badge sans identifiant n'a pas de fiche à
 * ouvrir. Le message, lui, reste dans le fil.
 */
function author(message: ConversationMessage): AccountFace | null {
  if (message.accountId === null || message.username === null) return null;

  return {
    avatarUrl: message.avatarUrl,
    id: message.accountId,
    username: message.username,
  };
}

type ConversationViewerProps = {
  conversationId: number;
  /** Ce que le bouton d'ouverture affiche. */
  label: string;
  /** Titre de la fenêtre — « le fil de l'événement », « la discussion ». */
  title: string;
};

/**
 * Une discussion, lue par la modération.
 *
 * Partagée par la fiche d'un compte et celle d'un événement : les deux ouvrent
 * la même route, `GET /moderation/conversations/{id}`, et deux rendus du même
 * fil auraient fini par diverger.
 *
 * ⚠️ **Le serveur journalise chaque lecture**, avec l'administrateur qui l'a
 * faite. C'est ce qui borne l'élargissement consenti par ce lot : `getReportContext`
 * n'ouvrait qu'un fil déjà signalé, celui-ci ouvre n'importe lequel de ceux
 * auxquels un compte participe. La contrepartie est la trace — et c'est
 * pourquoi le corps de la fenêtre n'est monté **qu'à l'ouverture** : un
 * chargement au rendu de la liste inscrirait au registre des consultations qui
 * n'ont pas eu lieu.
 */
export function ConversationViewer({
  conversationId,
  label,
  title,
}: ConversationViewerProps) {
  const [isOpen, setOpen] = useState(false);
  const t = useTranslations("conversations");

  return (
    <Modal isOpen={isOpen} onOpenChange={setOpen}>
      <Button size="sm" variant="ghost">
        {label}
      </Button>

      <Modal.Backdrop>
        <Modal.Container scroll="inside" size="lg">
          <Modal.Dialog>
            <Modal.Header>
              <Modal.Heading>{title}</Modal.Heading>
              {/* ⚠️ Le `CloseButton` de HeroUI annonce « Close » en anglais en
                  dur : sans cet `aria-label`, la fenêtre a un bouton
                  monolingue. */}
              <Modal.CloseTrigger aria-label={t("close")} />
            </Modal.Header>

            <Modal.Body className="flex flex-col gap-3">
              {isOpen ? <ViewerBody conversationId={conversationId} /> : null}
            </Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

function ViewerBody({ conversationId }: { conversationId: number }) {
  const t = useTranslations("conversations");
  const { locale } = useLocale();

  /*
    Le curseur de remontée. `undefined` = la dernière page ; sinon
    l'identifiant du plus ancien message déjà lu.

    ⚠️ **Un identifiant, pas un numéro de page** : sous `OFFSET`, un message
    envoyé pendant la lecture décale toute la suite, et l'on saute une ligne à
    chaque page tournée.
  */
  const [before, setBefore] = useState<number>();

  const { conversation, isLoading, isFetching, isError, isMissing } =
    useAdminConversation(conversationId, true, before);

  if (isLoading) {
    return (
      <div aria-hidden className="flex flex-col gap-3">
        {[0, 1, 2, 3].map((row) => (
          <Skeleton className="h-12 w-full" key={row} />
        ))}
      </div>
    );
  }

  // La discussion a disparu — supprimée avec son événement, le plus souvent.
  // Cas normal, pas une panne : d'où le ton, et l'absence de toast.
  if (isMissing) {
    return <p className="text-sm text-pretty text-muted">{t("gone")}</p>;
  }

  if (isError || !conversation) {
    return <p className="text-sm text-pretty text-muted">{t("failed")}</p>;
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
        <span>
          {t(conversation.type === "group" ? "kind-group" : "kind-direct")}
        </span>
        <span>·</span>
        <span>{t("message-count", { count: conversation.messageCount })}</span>

        {conversation.event ? (
          <Chip size="sm" variant="secondary">
            {t("event-thread", { name: conversation.event.name })}
          </Chip>
        ) : null}
      </div>

      {/*
        Qui est là, avant ce qui s'est dit : dans un fil à cinq voix, on lit
        d'abord la liste des présents. Les partis y restent — ils ont écrit — et
        se distinguent par leur pastille.
      */}
      <ul className="flex flex-wrap items-center gap-2">
        {conversation.participants.map((participant) => (
          <li className="flex items-center gap-1" key={participant.id}>
            <AccountBadge account={participant} isLinked />
            {participant.leftAt ? (
              <Chip size="sm" variant="secondary">
                {t("left")}
              </Chip>
            ) : null}
          </li>
        ))}
      </ul>

      {/*
        La borne dit ce qui n'est **pas** montré. Sans elle, une page de
        cinquante messages se lit comme la conversation entière.
      */}
      {conversation.hasMoreBefore ? (
        <Button
          className="self-center"
          isDisabled={isFetching}
          size="sm"
          variant="ghost"
          // Le plus ancien message de la page devient le curseur : la page
          // suivante est celle d'avant lui.
          onPress={() => setBefore(conversation.messages[0]?.id)}
        >
          {isFetching ? <Spinner color="current" size="sm" /> : null}
          {t("load-older")}
        </Button>
      ) : (
        <p className="text-center text-xs text-muted">{t("beginning")}</p>
      )}

      <ol className="flex flex-col gap-2">
        {conversation.messages.map((message) => (
          <li
            className={`flex flex-col gap-1 rounded-lg p-3 ${
              /*
                Un message système n'est pas une bulle : il annonce, il ne parle
                pas. Sans fond, il se lit comme l'annotation qu'il est — et
                surtout on ne prend pas « X a rejoint le groupe » pour une
                phrase tapée par X.

                ⚠️ Pas d'opacité choisie à la main (`bg-default/40` et
                consorts) : la palette a des jetons dosés pour les deux thèmes,
                et une valeur inventée ici tiendrait dans l'un seulement.
              */
              message.type === "user" ? "bg-surface" : ""
            }`}
            key={message.id}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">
                <AccountBadge account={author(message)} isLinked />
              </span>

              <span className="text-xs text-muted">
                {relativeTime(message.createdAt, locale, Date.now())}
              </span>

              {message.type === "user" ? null : (
                <Chip size="sm" variant="secondary">
                  {t("system")}
                </Chip>
              )}

              {message.isReported ? (
                <Chip color="warning" size="sm">
                  {t("reported")}
                </Chip>
              ) : null}

              {/*
                ⚠️ Le contenu d'un message supprimé est **conservé** ici, alors
                que l'endpoint utilisateur renvoie `null`. Écart assumé du
                serveur : la suppression est le plus souvent la suite donnée au
                signalement, la masquer effacerait la preuve au moment de
                relire le dossier. D'où le barré, et non l'absence.
              */}
              {message.deletedAt ? (
                <Chip color="danger" size="sm">
                  {t("deleted")}
                </Chip>
              ) : null}
            </div>

            <p
              className={`text-sm text-pretty whitespace-pre-wrap ${
                message.deletedAt ? "text-muted line-through" : ""
              }`}
            >
              {message.content}
            </p>

            <MessageReactions locale={locale} reactions={message.reactions} />
          </li>
        ))}
      </ol>

      {conversation.messages.length === 0 ? (
        <p className="text-center text-sm text-muted italic">{t("empty")}</p>
      ) : null}
    </>
  );
}

/**
 * Les réactions posées sur un message.
 *
 * Elles ne sont pas décoratives en modération : un pouce levé sur une insulte
 * change ce qu'on juge — une seule personne dérape, ou trois s'y mettent. D'où
 * l'affichage des **noms**, et pas seulement d'un compteur.
 *
 * ⚠️ Un emoji ne s'annonce pas. `aria-hidden` sur ce qui est visible, et la
 * phrase complète en `sr-only` : sans elle, un lecteur d'écran lit le nom
 * Unicode de l'emoji suivi d'un nombre nu, ce qui ne dit ni qui ni quoi.
 */
function MessageReactions({
  locale,
  reactions,
}: {
  locale: Locale;
  reactions: ConversationReaction[];
}) {
  const t = useTranslations("conversations");

  if (reactions.length === 0) return null;

  const list = new Intl.ListFormat(locale, { type: "conjunction" });

  return (
    <ul className="flex flex-wrap items-center gap-1.5 pt-1">
      {reactions.map((reaction) => {
        const names = list.format(
          reaction.accounts.map((account) => account.username),
        );

        return (
          <li key={reaction.emoji}>
            <span
              className="flex items-center gap-1 rounded-full bg-default px-2 py-0.5 text-xs text-default-foreground"
              title={names}
            >
              <span aria-hidden="true">{reaction.emoji}</span>
              <span aria-hidden="true" className="tabular-nums opacity-60">
                {reaction.accounts.length}
              </span>
              <span className="sr-only">
                {t("reaction-by", { emoji: reaction.emoji, names })}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
